import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { FanControlsNavigation } from '../component/grain-container/FanControlNavigationOption';

// ─── Bin-Scoped Fields ────────────────────────────────────────────────────────
// These fields are reset whenever the user navigates to a different grain bin.

export interface BinScopedFields {
  isFanStopTriggered: boolean;
  isFanStartTriggered: boolean;
  isUserOnTempRecommWindowsPage: boolean;
  initialFanControlCardNavigationType: FanControlsNavigation | undefined;
  fanScheduleDrafts: { start_epoch: Date | null; end_epoch: Date | null }[];
  /** Set by "Start Automation" to prevent recommendation type flip-flop during refetch race. */
  committedRecommendationType: string | null;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export interface BinScopedActions {
  setIsFanStopTriggered: (value: boolean) => void;
  setIsFanStartTriggered: (value: boolean) => void;
  setIsUserOnTempRecommWindowsPage: (value: boolean) => void;
  setInitialFanControlCardNavigationType: (value: FanControlsNavigation | undefined) => void;
  setFanScheduleDrafts: (drafts: { start_epoch: Date | null; end_epoch: Date | null }[]) => void;
  setCommittedRecommendationType: (value: string | null) => void;
  /** Resets only bin-scoped fields to defaults (on bin navigation). */
  resetBinState: () => void;
  /** Resets ALL fields (on account switch). */
  resetAll: () => void;
}

// ─── Initial Values ───────────────────────────────────────────────────────────

const binScopedDefaults: BinScopedFields = {
  isFanStopTriggered: false,
  isFanStartTriggered: false,
  isUserOnTempRecommWindowsPage: false,
  initialFanControlCardNavigationType: undefined,
  fanScheduleDrafts: [],
  committedRecommendationType: null,
};

// ─── Store ────────────────────────────────────────────────────────────────────

/**
 * Bin-scoped ephemeral state store — holds state that is specific to the
 * currently active grain bin and resets on container navigation.
 *
 * Connected to Redux DevTools via the `devtools` middleware for
 * time-travel debugging and state inspection in development.
 */
export const useBinScopedStore = create<BinScopedFields & BinScopedActions>()(
  devtools(
    (set) => ({
      ...binScopedDefaults,

      // Bin-scoped setters
      setIsFanStopTriggered: (value) => {
        set({ isFanStopTriggered: value }, undefined, 'setIsFanStopTriggered');
      },
      setIsFanStartTriggered: (value) => {
        set({ isFanStartTriggered: value }, undefined, 'setIsFanStartTriggered');
      },
      setIsUserOnTempRecommWindowsPage: (value) => {
        set(
          { isUserOnTempRecommWindowsPage: value },
          undefined,
          'setIsUserOnTempRecommWindowsPage'
        );
      },
      setInitialFanControlCardNavigationType: (value) => {
        set(
          { initialFanControlCardNavigationType: value },
          undefined,
          'setInitialFanControlCardNavigationType'
        );
      },
      setFanScheduleDrafts: (drafts) => {
        set({ fanScheduleDrafts: drafts }, undefined, 'setFanScheduleDrafts');
      },
      setCommittedRecommendationType: (value) => {
        set({ committedRecommendationType: value }, undefined, 'setCommittedRecommendationType');
      },

      // Reset only bin-scoped fields (on bin navigation)
      resetBinState: () => {
        set(binScopedDefaults, undefined, 'resetBinState');
      },

      // Reset everything (on account switch).
      // Currently identical to resetBinState because all account-scoped fields
      // (unreadNotificationCount, userLastNotificationsReadOn) were moved to Apollo cache.
      // Kept as a separate action for semantic clarity and forward-compatibility —
      // future account-scoped UI state (e.g. account-level prefs) would go here.
      resetAll: () => {
        set(binScopedDefaults, undefined, 'resetAll');
      },
    }),
    { name: 'BinScoped' }
  )
);
